import { ChallengeService } from '@duck/api-definitions'
import {
  PlayIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import { useTar } from '@/modules'
import { DevPrint } from '@components/DevPrint'
import { StandardLoader } from '../StandardLoader'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from '@tanstack/react-router'
import { useBootChallengeWithWeapon, WeaponPickCancelledError } from '@/hooks/useBootChallengeWithWeapon'
import { useState } from 'react'
import {
  challengeDifficultyDisplayMap,
  getChallengeDifficultyColor,
  timeCategoryDisplayMap
} from '@/utils/challengeData.utils'
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@duck/ui'
import { AllowedWeaponsPills } from '@components/AllowedWeaponsPills'
import { toast } from 'sonner'
import { availableChallengesQueryKeys } from '@/hooks/useAvailableChallenges'
import { queryClient } from '@/modules/queryClient'
import { CoverArt } from './CoverArt'
import { useChallengeAction } from './useChallengeAction'

type Challenge = ChallengeService.AvailableChallenge

interface ChallengeCardProps {
  challenge: Challenge
  /** Opens the detail drawer/sheet. Card body click triggers this. */
  onOpenDetail?: (challenge: Challenge) => void
  /** This card's challenge is the one open in the detail drawer. Gives it a
   *  primary border so it stays identifiable behind the dimmed overlay as the
   *  user navigates prev/next. */
  active?: boolean
}

export const ChallengeCard: React.FC<ChallengeCardProps> = ({
  challenge,
  onOpenDetail,
  active = false,
}) => {
  const { t } = useTar()
  const { loggedInAction } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const bootChallenge = useBootChallengeWithWeapon()
  const action = useChallengeAction(challenge)

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    loggedInAction('challenge-card-action', async () => {
      setIsLoading(true)
      try {
        if (challenge.status === ChallengeService.AvailableChallengeStatusEnum.Available) {
          const resp = await bootChallenge({
            taskId: challenge.taskId,
            allowedWeapons: challenge.allowedWeapons,
          })
          const slug = resp.data.slug
          if (slug) {
            sessionStorage.setItem('challenge_unbooted', slug)
            void queryClient.invalidateQueries({ queryKey: availableChallengesQueryKeys.all() })
            navigate({ to: `/challenge/${slug}` })
            return
          }
        }
        if (challenge.slug && challenge.status === ChallengeService.AvailableChallengeStatusEnum.Unbooted) {
          sessionStorage.setItem('challenge_unbooted', challenge.slug)
          navigate({ to: `/challenge/${challenge.slug}` })
          return
        }
        if (challenge.slug && challenge.status === ChallengeService.AvailableChallengeStatusEnum.Ongoing) {
          navigate({ to: `/challenge/${challenge.slug}` })
          return
        }
      } catch (err) {
        if (err instanceof WeaponPickCancelledError) return
        toast.error(t('challenges.failedToStartChallenge'), {
          duration: 10000
        })
      } finally {
        setIsLoading(false)
      }
    })
  }

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onOpenDetail?.(challenge)
  }

  return (
    <div
      data-testid={`challenge-card-${challenge.taskId}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetail?.(challenge)}
      onKeyDown={handleCardKeyDown}
      className={`group flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-card transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:outline-none ${active ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-accent'}`}
    >
      {/* Cover art with overlaid weapon pills + difficulty badge */}
      <div className="relative h-28">
        <CoverArt
          taskId={challenge.taskId}
          coverImageUrl={challenge.coverImageUrl}
          focusY={challenge.coverFocusY}
          alt={challenge.name}
          className="absolute inset-0 h-full w-full"
        />
        <div className="absolute inset-x-2.5 top-2.5 z-10 flex items-center gap-1.5">
          <AllowedWeaponsPills allowedWeapons={challenge.allowedWeapons} pillClassName="bg-card" />
          {!challenge.isSandbox && (
            <span className="ms-auto rounded-full bg-card">
              <span className={`block rounded-full border px-2 py-0.5 text-xs font-medium ${getChallengeDifficultyColor(challenge.difficulty)}`}>
                {t(challengeDifficultyDisplayMap[challenge.difficulty])}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <DevPrint label="challengeId" value={challenge.challengeId} />
        <DevPrint label="taskId" value={challenge.taskId} />

        <h3 className="line-clamp-2 text-base font-bold text-foreground">{challenge.name}</h3>
        <p className="line-clamp-2 text-sm text-muted-foreground">{challenge.teaser}</p>

        {/* Cooldown notice */}
        {challenge.status === ChallengeService.AvailableChallengeStatusEnum.Cooldown && (
          <div className="flex items-center justify-start text-muted text-xs">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center">
                  <span>{t('challenges.challengeInCooldown')}</span>
                  <QuestionMarkCircleIcon className="w-4 h-4 ms-1 inline" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('challenges.cooldownDescription')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Bottom row: time-category label + action + info buttons */}
        <div className="mt-auto flex items-center gap-2 pt-1">
          {!challenge.isSandbox && (
            <span className="font-mono text-xs text-muted-foreground">
              {t(timeCategoryDisplayMap[challenge.timeCategory])}
            </span>
          )}

          <Button
            data-testid="challenge-card-action-button"
            className={`ms-auto flex items-center justify-center gap-2 ${!isLoading ? action.className : ''}`}
            variant={isLoading ? 'outline' : 'default'}
            onClick={handleActionClick}
            disabled={isLoading || action.disabled}
          >
            {isLoading ? (
              <StandardLoader size="md" inline />
            ) : action.showsCooldownTime ? (
              <span>{action.label}</span>
            ) : (
              <>
                <PlayIcon className="w-4 h-4" />
                <span>{action.label}</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export type { Challenge, ChallengeCardProps }
